import styled, {css} from "@yb/styled-px2rem";
import {Select} from "@FRAMEWORK/ui/mobile/select";
import {useRef, useEffect, useState, useMemo, useCallback} from "react";
import withCalendar, {Calendar, WithCalendarRef} from "@COMPONENTS/hoc/withCalendar";
import {EventExt} from "@COMPONENTS/product/common/popup/desktop/EventListPopup/EventListPopup";
import dayjs, {Dayjs} from "dayjs";
import useContentsHoliday from "@QUERIES/essential/contents/useContentsHoliday";
import {format} from "@yb/utils";

interface DayProps {
	isActive?: boolean;
	CheckedDay?: boolean;
	today?: boolean;
	holiday?: boolean;
}

const CalendarWrap = styled.div`
	position: relative;
	width: 100%;
	padding: 70px 0 30px;
`;

const CalendarBox = styled.div<DayProps>`
	width: 100%;
	background-color: #fff;

	table {
		tbody {
			tr {
				border-top: 1px solid var(--line-color);
			}
		}
		th {
			background-color: #fff;
			text-align: center;
			border-top: 0;
		}
		td {
			position: relative;
			height: 45px;
			padding: 0;
			&:not(:last-child) {
				border-right: 1px solid #eee;
			}

			&:first-child {
				color: var(--red-color);
			}
			&:last-child {
				color: var(--blue-color);
			}
		}
	}
`;

const CalendarMonth = styled.div<{isScroll: boolean}>`
	position: absolute;
	top: 14px;
	display: flex;
	width: 100%;
	height: 50px;
	background-color: #fff;
	justify-content: center;
	align-items: center;
	z-index: 5;

	em {
		font-size: 22px;
		font-weight: bold;
		color: var(--emp-color);
		line-height: 1;
	}
	label {
		font-size: var(--medium-size);
		font-weight: normal;
	}
	ul {
		top: 50px;
		left: 50%;
		width: 100%;
		max-height: 155px;
		transform: translateX(-50%);
		> li {
			justify-content: center;
			padding: 0;
		}
	}
	> div {
		width: auto;
	}

	${props =>
		props.isScroll &&
		`
		position: fixed;
		top: 60px;
		border-bottom: 1px solid var(--line-color);
		transition: 0.3s ease-out;
	`}
`;

const MonthButton = styled.button<{types?: "prev" | "next"}>`
	position: absolute;
	top: 18px;
	width: 12px;
	height: 12px;
	border-left: 1px solid var(--emp-color);
	border-bottom: 1px solid var(--emp-color);
	background-color: transparent;

	&:disabled {
		border-color: var(--bl-color);
	}

	${props =>
		props.types === "prev"
			? `
                left: 24%;
                transform: rotate(45deg);
            `
			: `
                right: 29%;
                transform: rotate(-135deg);
            `}
`;

const TableCalendarWrap = styled.div``;

const TableCalendar = styled.table`
	width: 100%;
	border: 1px solid transparent;
	border-bottom: 1px solid var(--line-color);
	table-layout: fixed;
`;

const Week = styled.th<DayProps>`
	padding: 10px;
	box-sizing: border-box;
	border-top: 1px solid var(--line-color);
	font-size: var(--small-size);
	color: var(--emp-color);
	text-align: center;
	&:first-child {
		color: #ff5c46;
	}
	&:last-child {
		color: #4567ad;
	}
`;

const ProductDays = styled.td<DayProps>`
	position: relative;
	box-sizing: border-box;
	height: 22px;
	color: ${props => (props.isActive ? "var(--emp-color)" : "#aaa")};
	vertical-align: top;
	text-align: left;
	cursor: ${props => (props.isActive ? "pointer" : "initial")};

	> span {
		outline: 1px solid ${props => (props.CheckedDay ? "var(--emp-color)" : "transparent")};
		box-sizing: border-box;
	}
`;

const Day = styled.span<DayProps>`
	display: flex;
	width: 100%;
	height: 100%;
	padding-top: 2px;
	align-items: start;
	justify-content: center;
	z-index: 1;

	color: ${props => (props.holiday ? "var(--red-color)" : "")};

	span {
		display: flex;
		height: 18px;
		padding: 0 4px 0 2px;
		font-size: var(--small-size);
		align-items: center;
		justify-content: center;
		line-height: 1;
	}

	${props =>
		props.today &&
		css`
			span {
				border-radius: 50%;
				background-color: var(--emp-color);
				color: #fff;
			}
		`}
`;

const ResStatus = styled.div<{state?: string; lowestPrice?: boolean}>`
	position: absolute;
	display: flex;
	bottom: 0;
	width: 100%;
	height: 22px;
	font-size: 12px;
	color: var(--default-color);
	align-items: center;
	justify-content: center;

	${props => {
		if (props.state === "possible") {
			return `
			background-color: var(--yellow-color);
		  `;
		}
		if (props.state === "waiting") {
			return `
			background-color: #bfd1f5;
		  `;
		}
		if (props.state === "deadline") {
			return `
			background-color: var(--line-color);
		  `;
		}
	}}
	${props =>
		props.lowestPrice &&
		css`
			color: #ff3e24;
		`}
`;

const CalendarButton = styled.button`
	position: absolute;
	top: 50%;
	right: 15px;
	border-bottom: 1px solid var(--emp-color);
	font-size: var(--defaul-size);
	color: var(--emp-color);
	line-height: 1;
	transform: translateY(-50%);
`;

const FixedCalendar = styled.div`
	position: fixed;
	top: 109px;
	padding-bottom: 30px;
	border-bottom: 1px solid var(--line-color);
	background-color: #fff;
	z-index: 5;
`;

const Dim = styled.div`
	position: fixed;
	top: 0;
	height: 100vh;
	width: 100%;
	background-color: #000;
	opacity: 0.5;
	z-index: 1;
`;

type EventDay = {
	day: Dayjs;
	/** 예약상태 */
	resStatus: string;
	/** 공휴일 */
	holiday: boolean;
	/** day행사중 최저가 */
	price: number;
};

const formatPrice = prop => {
	if (prop < 10000) {
		return `${format.koKRFormatNumber(prop)}원~`;
	}
	const million = Math.floor(prop / 10000);
	return `${million}만~`;
};

const EventListCalendar = withCalendar(
	(
		props: Calendar & {
			data: {minPrice: string; eventList: Array<EventExt>};
		},
		ref: WithCalendarRef,
	) => {
		const {date, isNext, isPrevious, rangeMonth, month, data, dateChange, monthChange, nextMonth, prevMonth} = props;
		// 스크롤시 달력이 안보이면 달력 헤더 고정
		const monthRef = useRef<HTMLDivElement | null>(null);
		const calendarRef = useRef<HTMLTableElement | null>(null);
		const [isShow, setIsShow] = useState(false);

		useEffect(() => {
			const options = {
				root: null,
				rooMargin: "0px",
				threshold: 0,
			};

			const observer = new IntersectionObserver(entries => {
				entries.forEach(entry => {
					if (entry.target === calendarRef.current) {
						if (entry.isIntersecting) {
							// 달력이 화면에 보일 때
							setIsShow(false);
						} else {
							// 달력이 화면에 안 보일 때
							setIsShow(true);
						}
					}
				});
			}, options);

			if (calendarRef.current) {
				observer.observe(calendarRef.current);
			}

			return () => {
				observer.disconnect();
			};
		}, []);

		const [isActive, setIsActive] = useState(false);
		const handleClick = () => {
			setIsActive(!isActive);
			if (isActive) {
				document.body.style.cssText = `
				overflow: hidden;
				`;
			} else {
				document.body.style.cssText = `
			overflow: hidden;
			height: 100vh;
			`;
			}
		};

		const eventList = data?.eventList;
		const minPrice = data?.minPrice;

		const holidayFetch = useContentsHoliday(dayjs(month).format("YYYY"), {staleTime: 1000 * 60 * 5});
		const today = dayjs();

		const groupByEvStartDt = useMemo(() => {
			const result = {};
			if (!eventList) return result;
			for (const item of eventList) {
				const outStartDt = item.outStartDt;
				if (!result[outStartDt]) {
					result[outStartDt] = [];
				}
				result[outStartDt].push(item);
			}
			return result;
		}, [eventList, month]);

		// 날짜 매핑
		const makeEventDayList = useCallback(
			(referenceDate: Dayjs): Array<EventDay> => {
				const dayCount = referenceDate.date(1).daysInMonth();
				/** 1~7 월~일 */
				const startWeekDay = referenceDate.day();
				const endWeekDay = referenceDate.date(dayCount).day();
				const firstWeekEmptyArray = Array(startWeekDay).fill(null);
				const lastWeekEmptyArray = Array(6 - endWeekDay).fill(null);
				const dayArray = Array(dayCount)
					.fill(0)
					.map((o, index) => {
						let result = {};
						const day = referenceDate.add(index, "day");
						const dayFormat = day.format("YYYYMMDD");
						/* 공휴일 */
						const holiday = !!holidayFetch.data?.body?.find(v => v.calDt === dayFormat);
						/* 최저가 */
						const priceArray: Array<number> = groupByEvStartDt[dayFormat]?.map(item => item.sumPrice) || [];
						const price = priceArray.length > 0 ? Math.min(...priceArray) : 0;
						/* can: 예약가능, wait: 예약대기, end: 예약마감 */
						const calStatusArray: Array<string> = groupByEvStartDt[dayFormat]?.map(item => item.evReserveStatus) || [];
						let resStatus = "";
						if (calStatusArray.includes("can")) {
							resStatus = "possible";
						} else if (calStatusArray.includes("wait")) {
							resStatus = "waiting";
						} else if (calStatusArray.includes("end")) {
							resStatus = "deadline";
						}

						result = {day, resStatus, price, holiday};
						return result;
					});
				return [...firstWeekEmptyArray, ...dayArray, ...lastWeekEmptyArray];
			},
			[groupByEvStartDt],
		);

		// eveny day를 주 단위로 삽입
		const eventDayList = useMemo(() => {
			const days = makeEventDayList(month.date(1));
			return Array.from({length: days.length / 7}).map(() => days.splice(0, 7));
		}, [makeEventDayList]);

		/** default 값 초기화 */
		useEffect(() => {
			if (month) dateChange(null);
		}, []);

		return (
			<CalendarWrap>
				<CalendarBox>
					<CalendarMonth ref={monthRef} isScroll={isShow}>
						<Select
							types="calendar"
							width="auto"
							value={month.format("YYYYMM")}
							onChange={e => {
								monthChange(e.target.value.toString());
								dateChange(null); // 선택일 초기화
							}}
						>
							{rangeMonth?.map((day: Dayjs, index) => (
								<option key={index} value={day.format("YYYYMM")}>
									{day.format("YYYY.MM")}
								</option>
							))}
						</Select>
						{isPrevious && (
							<MonthButton
								types="prev"
								onClick={() => {
									prevMonth();
									dateChange(null);
								}}
							>
								<span>이전달</span>
							</MonthButton>
						)}
						{isNext && (
							<MonthButton
								types="next"
								onClick={() => {
									nextMonth();
									dateChange(null);
								}}
							>
								<span>다음달</span>
							</MonthButton>
						)}
						{isShow && <CalendarButton onClick={handleClick}>{isActive ? "닫기" : "출발일 선택"}</CalendarButton>}
					</CalendarMonth>
					<TableCalendarWrap ref={calendarRef}>
						<TableCalendar>
							<thead>
								<tr>
									<Week>일</Week>
									<Week>월</Week>
									<Week>화</Week>
									<Week>수</Week>
									<Week>목</Week>
									<Week>금</Week>
									<Week>토</Week>
								</tr>
							</thead>
							<tbody>
								{eventDayList?.map((week, index) => (
									<tr key={index}>
										{week.map((item, weekIdx) => {
											const day = item?.day as unknown as dayjs.Dayjs;
											const price = item?.price || 0;
											const isDayActive = (!(day < today.add(-1, "day")) && price > 0) || !!item?.resStatus;
											return (
												<ProductDays
													isActive={isDayActive}
													onClick={() => {
														if (isDayActive) {
															const isSameDay = date?.isSame(day);
															dateChange(!isSameDay ? day : null);
														}
													}}
													key={weekIdx}
													CheckedDay={date && date.isSame(day)}
												>
													<Day today={day && today.isSame(day, "day")} holiday={item?.holiday}>
														<span>{day && day.format("DD")}</span>
													</Day>
													{price > 0 && (
														<ResStatus state={item?.resStatus || ""} lowestPrice={price === Number(minPrice)}>
															{formatPrice(price)}
														</ResStatus>
													)}
												</ProductDays>
											);
										})}
									</tr>
								))}
							</tbody>
						</TableCalendar>
					</TableCalendarWrap>
					{isActive && isShow && (
						<>
							<FixedCalendar>
								<TableCalendar>
									<thead>
										<tr>
											<Week>일</Week>
											<Week>월</Week>
											<Week>화</Week>
											<Week>수</Week>
											<Week>목</Week>
											<Week>금</Week>
											<Week>토</Week>
										</tr>
									</thead>
									<tbody>
										{eventDayList?.map((week, index) => (
											<tr key={index}>
												{week.map((item, weekIdx) => {
													const day = item?.day as unknown as dayjs.Dayjs;
													const price = item?.price || 0;
													const isDayActive = (!(day < today.add(-1, "day")) && price > 0) || !!item?.resStatus;
													return (
														<ProductDays
															isActive={isDayActive}
															onClick={() => {
																if (isDayActive) {
																	const isSameDay = date?.isSame(day);
																	dateChange(!isSameDay ? day : null);

																	// 달력 닫기
																	handleClick();
																}
															}}
															key={weekIdx}
															CheckedDay={date && date.isSame(day)}
														>
															<Day today={day && today.isSame(day, "day")} holiday={item?.holiday}>
																<span>{day && day.format("DD")}</span>
															</Day>
															{price > 0 && (
																<ResStatus state={item?.resStatus || ""} lowestPrice={price === Number(minPrice)}>
																	{formatPrice(price)}
																</ResStatus>
															)}
														</ProductDays>
													);
												})}
											</tr>
										))}
									</tbody>
								</TableCalendar>
							</FixedCalendar>
							<Dim onClick={() => setIsActive(false)} />
						</>
					)}
				</CalendarBox>
			</CalendarWrap>
		);
	},
);

export default EventListCalendar;
